import { HikeHighlightPayload, poiToHikeHighlight } from "../../../utils/helpers/PoiToHikeHighlightHelper";
import { poiStageDestination } from "../../map_data_poi_dialog/helpers/poiStageDestination";
import { NearbyPlace, placeTypeLabel } from "./route-endpoints";
import type { StageDestination } from "./stage-destinations";
import type { GooglePlace } from "../../../utils/services/GoogleMapsPlaces";

export {
  GOOGLE_RESULT_RANGE_M,
  googlePlacesFromSerp,
  googlePlaceType,
  rankGooglePlaces,
} from "../../../utils/services/GoogleMapsPlaces";
export type { GooglePlace } from "../../../utils/services/GoogleMapsPlaces";

/**
 * Something picked in the place search (PlaceSearchPanel.vue) for a route's
 * end or a night's stay — one of our places, a place found on Google Maps, or
 * a plain address — and what each becomes on the plan. Pure.
 */

export type PlacePick =
  | { kind: 'place'; place: NearbyPlace }
  | { kind: 'google'; place: GooglePlace }
  | { kind: 'address'; name: string; coordinates: [number, number] };

export function pickCoordinates(pick: PlacePick): [number, number] {
  return pick.kind === 'address' ? pick.coordinates : pick.place.coordinates;
}

export function pickName(pick: PlacePick): string {
  return pick.kind === 'address' ? pick.name : pick.place.name;
}

/** "Hotel", "Bed & breakfast" — what kind of place it is, when it's a place. */
export function pickTypeLabel(pick: PlacePick): string | null {
  if (pick.kind === 'place') return placeTypeLabel(pick.place.type);
  if (pick.kind === 'google') return pick.place.category || placeTypeLabel(pick.place.type);
  return null;
}

/**
 * The highlight a Google Maps place becomes on the plan, so the stop has
 * something to be pinned to — null for anything else (our own places become
 * highlights through highlightForDestination; an address isn't a place).
 */
export function pickHighlight(
  pick: PlacePick,
  dayNumber: number,
  clientId: string
): (HikeHighlightPayload & { dayNumber: number; clientId: string }) | null {
  if (pick.kind !== 'google') return null;
  const [lng, lat] = pick.place.coordinates;
  return {
    ...poiToHikeHighlight({ type: pick.place.type, name: pick.place.name, description: pick.place.address, lng, lat }),
    dayNumber,
    clientId,
  };
}

/**
 * What the stop is pinned to: one of our places directly, a Google place
 * through the highlight made for it (`highlightClientId`), an address not at
 * all.
 */
export function pickDestination(pick: PlacePick, highlightClientId?: string | null): StageDestination | null {
  if (pick.kind === 'place') {
    const [lng, lat] = pick.place.coordinates;
    return poiStageDestination({
      poiType: pick.place.category, type: pick.place.type, poiId: pick.place.id, name: pick.place.name, lngLat: { lng, lat },
    }) as StageDestination;
  }
  if (pick.kind === 'google' && highlightClientId) {
    return {
      itemType: 'highlight',
      itemId: highlightClientId,
      name: pick.place.name,
      poiType: pick.place.type,
      coordinates: pick.place.coordinates,
    } as StageDestination;
  }
  return null;
}
